
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Menu, X } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/lib/AuthContext";

const LandingHeader = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLogoClick = () => {
    navigate(user ? "/home" : "/");
  };

  const handleNavClick = (id: string) => {
    setMobileMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? "py-2 bg-background/80 backdrop-blur-md border-b shadow-sm" 
          : "py-4 bg-transparent"
      }`}
    >
      <div className="container mx-auto flex justify-between items-center px-4">
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="flex items-center gap-2 cursor-pointer" 
          onClick={handleLogoClick}
        >
          <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center">
            <span className="text-primary-foreground font-bold text-lg">C</span>
          </div>
          <span className="text-xl font-semibold">Chito</span>
        </motion.div>
        
        {/* Desktop Navigation */}
        <motion.nav 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="hidden md:flex items-center gap-8"
        >
          <button onClick={() => handleNavClick('features')} className="text-foreground/80 hover:text-foreground transition-colors">Features</button>
          <button onClick={() => handleNavClick('pricing')} className="text-foreground/80 hover:text-foreground transition-colors">Pricing</button>
          <a href="#" className="text-foreground/80 hover:text-foreground transition-colors">About</a>
        </motion.nav>
        
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="hidden md:block"
        >
          <Button 
            variant="default" 
            size="sm" 
            className="flex items-center gap-2 rounded-md"
            onClick={() => navigate(user ? "/home" : "/login")}
          >
            {user ? "Dashboard" : "Login"} 
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /> 
          </Button>
        </motion.div>
        
        {/* Mobile Menu Button */}
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="md:hidden"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </motion.button>
      </div>
      
      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="md:hidden bg-background border-b"
        >
          <div className="container mx-auto px-4 py-4 flex flex-col space-y-4">
            <button 
              onClick={() => handleNavClick('features')} 
              className="text-foreground/80 hover:text-foreground py-2 transition-colors text-left"
            >
              Features
            </button>
            <button 
              onClick={() => handleNavClick('pricing')} 
              className="text-foreground/80 hover:text-foreground py-2 transition-colors text-left"
            >
              Pricing
            </button>
            <a 
              href="#" 
              className="text-foreground/80 hover:text-foreground py-2 transition-colors text-left"
            >
              About
            </a>
            <Button 
              variant="default" 
              size="sm" 
              className="flex items-center gap-2 w-full justify-center"
              onClick={() => {
                setMobileMenuOpen(false);
                navigate(user ? "/home" : "/login");
              }}
            >
              {user ? "Dashboard" : "Login"}
              <ArrowRight className="h-4 w-4" /> 
            </Button>
          </div>
        </motion.div>
      )}
    </header>
  );
};

export default LandingHeader;
